import { useState } from "react";
import { Table, Tag } from "antd";
import { ShieldCheck } from "lucide-react";
import type { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { useNMWList } from "../../../hooks/api/payslips/hooks";

const formatCurrency = (v: any) =>
  v != null ? `£${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2 })}` : "—";

const formatDate = (v: any) => (v ? dayjs(v).format("DD MMM YYYY") : "—");

const complianceColor = (s: string) => {
  if (!s) return "default";
  const sl = s.toLowerCase();
  if (sl === "compliant" || sl === "pass" || sl === "passed") return "green";
  if (sl === "warning" || sl === "review") return "orange";
  if (sl === "non-compliant" || sl === "fail" || sl === "failed") return "red";
  return "default";
};

const NMWPage = () => {
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data: res, isLoading } = useNMWList({ page, limit });
  const records: any[] = (res?.data as any) ?? [];
  const total = Number(res?.extra?.total ?? 0);

  const columns: ColumnType<any>[] = [
    {
      title: "Worker",
      key: "worker",
      render: (_: any, r: any) =>
        r.worker_name || r.worker || r.name || r.employee_name || "—",
      width: 180,
    },
    {
      title: "Pay Rate (£/hr)",
      key: "pay_rate",
      render: (_: any, r: any) =>
        r.hourly_rate != null
          ? `£${Number(r.hourly_rate).toFixed(2)}/hr`
          : r.pay_rate != null
          ? `£${Number(r.pay_rate).toFixed(2)}/hr`
          : "—",
      width: 130,
    },
    {
      title: "NMW Rate (£/hr)",
      key: "nmw_rate",
      render: (_: any, r: any) =>
        r.nmw_rate != null
          ? `£${Number(r.nmw_rate).toFixed(2)}/hr`
          : r.minimum_wage != null
          ? `£${Number(r.minimum_wage).toFixed(2)}/hr`
          : "—",
      width: 130,
    },
    {
      title: "Gross Pay",
      key: "gross",
      render: (_: any, r: any) =>
        formatCurrency(r.gross_pay ?? r.gross_payment ?? r.gross_amount ?? r.gross),
      width: 120,
    },
    {
      title: "Hours Worked",
      key: "hours",
      render: (_: any, r: any) =>
        r.hours_worked != null ? `${Number(r.hours_worked).toFixed(1)} hrs` : "—",
      width: 120,
    },
    {
      title: "Age Band",
      key: "age_band",
      render: (_: any, r: any) => r.age_band || r.age_category || r.age_group || "—",
      width: 120,
    },
    {
      title: "Period",
      key: "period",
      render: (_: any, r: any) =>
        r.pay_period || r.period || (r.period_start ? `${formatDate(r.period_start)} – ${formatDate(r.period_end)}` : "—"),
      width: 160,
    },
    {
      title: "Compliance",
      key: "compliance",
      render: (_: any, r: any) => {
        const s = r.compliance_status || r.status || r.nmw_compliance;
        return s ? (
          <Tag color={complianceColor(s)}>{String(s).toUpperCase()}</Tag>
        ) : (
          "—"
        );
      },
      width: 130,
    },
    {
      title: "Checked",
      key: "checked",
      render: (_: any, r: any) =>
        formatDate(r.checked_at ?? r.check_date ?? r.created_at),
      width: 120,
    },
  ];

  return (
    <div className="p-6 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck size={20} className="text-slate-500" />
            <h1 className="text-xl font-semibold text-slate-800">NMW Compliance</h1>
          </div>
          <p className="text-slate-500 text-sm mt-0.5">
            National Minimum Wage compliance records
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <Table
          dataSource={records}
          columns={columns}
          rowKey={(r) => r.id ?? r._id ?? Math.random()}
          loading={isLoading}
          locale={{
            emptyText: (
              <div className="py-12 text-center text-slate-400">
                <ShieldCheck size={40} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium">No NMW records found</p>
                <p className="text-sm mt-1">
                  NMW compliance data will appear here once records are generated.
                </p>
              </div>
            ),
          }}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showTotal: (t) => `${t} records`,
            showSizeChanger: false,
            onChange: (p) => setPage(p),
          }}
          scroll={{ x: 1000 }}
          size="small"
        />
      </div>
    </div>
  );
};

export default NMWPage;
